import React, { useState, useMemo } from 'react';
import { Box, Typography, CircularProgress, styled, keyframes } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { tradePackagesAPI, playersAPI } from '../../../utils/api';

const expand = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled(Box)({
  width: '100%',
  marginTop: '32px',
});

const Toggle = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  width: '100%',
  padding: '14px 24px',
  background: '#F5F5F7',
  border: '1px solid #EBEBED',
  borderRadius: '14px',
  color: '#6E6E73',
  fontSize: '14px',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: '#EBEBED',
    color: '#1D1D1F',
  },
});

const Chevron = styled(KeyboardArrowDownIcon)(({ open }) => ({
  fontSize: '20px',
  transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
}));

const Panel = styled(Box)({
  marginTop: '12px',
  background: '#F5F5F7',
  border: '1px solid #EBEBED',
  borderRadius: '18px',
  padding: '24px',
  animation: `${expand} 0.3s cubic-bezier(0.16, 1, 0.3, 1)`,
  '@media (max-width: 600px)': { padding: '20px' },
});

const TwoCol = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '32px',
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
    gap: '24px',
  },
});

const ColumnHeader = styled(Typography)({
  color: '#1D1D1F',
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  marginBottom: '4px',
});

const ColumnSub = styled(Typography)({
  color: '#86868B',
  fontSize: '11px',
  fontWeight: 500,
  letterSpacing: '1.5px',
  textTransform: 'uppercase',
  marginBottom: '16px',
});

const PositionGroup = styled(Box)({
  marginBottom: '16px',
  '&:last-child': { marginBottom: 0 },
});

const PositionLabel = styled(Typography)({
  color: '#86868B',
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '1.5px',
  textTransform: 'uppercase',
  marginBottom: '6px',
});

const PlayerRow = styled(Box)(({ inTrade }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '6px 10px',
  borderRadius: '8px',
  background: inTrade ? '#FFFFFF' : 'transparent',
  marginBottom: '2px',
  transition: 'background 0.2s ease',
  '&:hover': {
    background: inTrade ? '#FFFFFF' : '#EBEBED',
  },
}));

const PlayerName = styled(Typography)({
  color: '#1D1D1F',
  fontSize: '14px',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  minWidth: 0,
});

const PlayerTeamBadge = styled(Typography)({
  color: '#86868B',
  fontSize: '10px',
  fontWeight: 500,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  flexShrink: 0,
  marginLeft: '8px',
});

const InTradeBadge = styled(Typography)({
  fontSize: '9px',
  fontWeight: 700,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  color: '#FFFFFF',
  background: '#000000',
  borderRadius: '4px',
  padding: '2px 6px',
  marginLeft: '8px',
  flexShrink: 0,
});

const POSITION_ORDER = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];

const groupByPosition = (playerIds, playerData) => {
  const groups = {};
  POSITION_ORDER.forEach((p) => {
    groups[p] = [];
  });
  groups.OTHER = [];

  playerIds.forEach((pid) => {
    const p = playerData?.[pid];
    if (!p) {
      groups.OTHER.push({ id: pid, name: `Player ${pid}`, position: '?', team: null });
      return;
    }
    const pos = (p.position || 'OTHER').toUpperCase();
    const bucket = POSITION_ORDER.includes(pos) ? pos : 'OTHER';
    groups[bucket].push({
      id: pid,
      name: p.name || `Player ${pid}`,
      position: pos,
      team: p.team,
      ktcSf: p.ktc_value_sf,
      ktc1qb: p.ktc_value_1qb,
    });
  });

  // Sort within each group by KTC descending so studs are first
  Object.keys(groups).forEach((k) => {
    groups[k].sort((a, b) => (b.ktcSf || 0) - (a.ktcSf || 0));
  });

  return groups;
};

const RostersPanel = ({ uniqueLink, trade, playerData: tradePlayerData, isCreator = false }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rosters, setRosters] = useState(null);
  const [rosterPlayerData, setRosterPlayerData] = useState({});
  const [error, setError] = useState(null);

  // The set of player IDs that are part of THIS trade (so we can highlight them).
  const inTradeIds = useMemo(() => {
    const set = new Set();
    (trade?.creatorItems || []).forEach((i) => {
      if (i.type === 'player' && i.playerId) set.add(String(i.playerId));
    });
    (trade?.recipientItems || []).forEach((i) => {
      if (i.type === 'player' && i.playerId) set.add(String(i.playerId));
    });
    return set;
  }, [trade]);

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !rosters && !loading) {
      setLoading(true);
      setError(null);
      try {
        const res = await tradePackagesAPI.getRosters(uniqueLink);
        const data = res.data;
        setRosters(data);

        const allIds = new Set();
        (data.creator?.playerIds || []).forEach((id) => allIds.add(String(id)));
        (data.recipient?.playerIds || []).forEach((id) => allIds.add(String(id)));

        // Don't refetch players we already have from the trade
        const idsToFetch = Array.from(allIds).filter(
          (id) => !tradePlayerData?.[id]
        );

        if (idsToFetch.length > 0) {
          const playerRes = await playersAPI.getBatch(idsToFetch);
          setRosterPlayerData(playerRes.data.players || {});
        }
      } catch (err) {
        setError('Could not load rosters.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Combined player lookup: trade player data + roster player data
  const allPlayerData = useMemo(
    () => ({ ...rosterPlayerData, ...tradePlayerData }),
    [rosterPlayerData, tradePlayerData]
  );

  const renderRosterColumn = (roster, label) => {
    if (!roster) return null;
    const groups = groupByPosition(roster.playerIds || [], allPlayerData);
    const totalPlayers = (roster.playerIds || []).length;
    return (
      <Box>
        <ColumnHeader>{label}</ColumnHeader>
        <ColumnSub>
          {roster.displayName} · {totalPlayers}{' '}
          {totalPlayers === 1 ? 'player' : 'players'}
        </ColumnSub>
        {[...POSITION_ORDER, 'OTHER'].map((pos) => {
          const players = groups[pos] || [];
          if (players.length === 0) return null;
          return (
            <PositionGroup key={pos}>
              <PositionLabel>{pos === 'OTHER' ? '—' : pos}</PositionLabel>
              {players.map((p) => {
                const isInTrade = inTradeIds.has(String(p.id));
                return (
                  <PlayerRow key={p.id} inTrade={isInTrade}>
                    <PlayerName>{p.name}</PlayerName>
                    {isInTrade ? (
                      <InTradeBadge>In trade</InTradeBadge>
                    ) : p.team ? (
                      <PlayerTeamBadge>{p.team}</PlayerTeamBadge>
                    ) : null}
                  </PlayerRow>
                );
              })}
            </PositionGroup>
          );
        })}
        {(roster.picks || []).length > 0 && (
          <PositionGroup>
            <PositionLabel>Picks ({roster.picks.length})</PositionLabel>
            {roster.picks.map((pick) => (
              <PlayerRow key={pick.id}>
                <PlayerName>
                  {pick.displayName || `${pick.season} R${pick.round}`}
                </PlayerName>
                {pick.tier && (
                  <PlayerTeamBadge>{pick.tier}</PlayerTeamBadge>
                )}
              </PlayerRow>
            ))}
          </PositionGroup>
        )}
      </Box>
    );
  };

  return (
    <Wrapper>
      <Toggle onClick={handleToggle} role="button" tabIndex={0}>
        <span>{open ? 'Hide rosters' : 'See both rosters'}</span>
        <Chevron open={open ? 1 : 0} />
      </Toggle>

      {open && (
        <Panel>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
              <CircularProgress size={24} sx={{ color: '#86868B' }} />
            </Box>
          )}
          {error && (
            <Typography
              sx={{
                color: '#6E6E73',
                textAlign: 'center',
                fontSize: '14px',
                padding: '24px',
              }}
            >
              {error}
            </Typography>
          )}
          {!loading && !error && rosters && (
            <TwoCol>
              {isCreator ? (
                <>
                  {renderRosterColumn(rosters.creator, 'Your roster')}
                  {renderRosterColumn(rosters.recipient, 'Their roster')}
                </>
              ) : (
                <>
                  {renderRosterColumn(rosters.recipient, 'Your roster')}
                  {renderRosterColumn(rosters.creator, 'Their roster')}
                </>
              )}
            </TwoCol>
          )}
        </Panel>
      )}
    </Wrapper>
  );
};

export default RostersPanel;
